import React from 'react';
import { Box, Typography, Stack, Slider, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

// Mobile-First Styled Components
const SelectorContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gap: '14px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  [theme.breakpoints.up('md')]: {
    fontSize: '10px',
    fontWeight: 400,
    color: '#121927',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
}));

const RadiusDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: '#F8FAFC',
  borderRadius: '10px',
  border: '1px solid #E2E8F0',
  [theme.breakpoints.up('sm')]: {
    padding: '14px 18px',
  },
  [theme.breakpoints.up('md')]: {
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
  },
}));

const RadiusValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#0B91D6',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const UnitSelector = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '10px',
  [theme.breakpoints.up('md')]: {
    gap: '14px',
  },
}));

const UnitButton = styled(Box)(({ theme, selected }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: selected ? '#ffffff' : '#64748b',
  backgroundColor: selected ? '#0B91D6' : '#ffffff',
  padding: '10px 20px',
  borderRadius: '8px',
  cursor: 'pointer',
  border: selected ? 'none' : '1px solid #E2E8F0',
  transition: 'all 0.2s ease',
  textAlign: 'center',
  minWidth: '70px',
  flex: 1,
  '&:hover': {
    backgroundColor: selected ? '#0980C2' : '#F8FAFC',
    borderColor: selected ? 'transparent' : '#0B91D6',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  [theme.breakpoints.up('sm')]: {
    flex: 'none',
    minWidth: '80px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
    fontWeight: 400,
    color: selected ? '#ffffff' : '#121927',
    backgroundColor: selected ? '#34A853' : '#ffffff',
    padding: '8px 16px',
    borderRadius: '4px',
    flex: 'none',
    '&:hover': {
      backgroundColor: selected ? '#10B981' : '#F3F4F6',
    },
  },
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  padding: '8px 4px',
  [theme.breakpoints.up('sm')]: {
    padding: '8px 8px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 8px',
  },
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: '#0B91D6',
  height: 6,
  '& .MuiSlider-track': {
    border: 'none',
    height: 6,
  },
  '& .MuiSlider-rail': {
    opacity: 0.3,
    backgroundColor: '#E2E8F0',
    height: 6,
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '3px solid currentColor',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 4px 12px rgba(11, 145, 214, 0.3)',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: '#0B91D6',
    borderRadius: '6px',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: 600,
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#E2E8F0',
    height: 8,
    width: 2,
    marginTop: -1,
    '&.MuiSlider-markActive': {
      backgroundColor: 'currentColor',
    },
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 420,
    '& .MuiSlider-thumb': {
      height: 20,
      width: 20,
      border: '2px solid currentColor',
    },
  },
}));

const RadiusSelector = ({ value, selectedUnit, onValueChange, onUnitChange, isMobile, isTablet }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Generate marks for the slider
  const marks = [
    { value: 1, label: '1' },
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
  ];

  return (
    <SelectorContainer>
      <FieldLabel>Radius*</FieldLabel>

      {/* Current Value Display - Enhanced on mobile */}
      <RadiusDisplay>
        <RadiusValue>{value} {selectedUnit}</RadiusValue>
        {isSmallScreen && (
          <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
            Drag to adjust
          </Typography>
        )}
      </RadiusDisplay>

      {/* Slider */}
      <SliderContainer>
        <StyledSlider
          value={typeof value === 'number' ? value : 1}
          min={1}
          max={20}
          step={1}
          marks={isSmallScreen ? marks : true}
          valueLabelDisplay="auto"
          onChange={(_, v) => onValueChange(v)}
        />
      </SliderContainer>

      {/* Unit Selector */}
      <UnitSelector>
        <UnitButton
          selected={selectedUnit === 'Km'}
          onClick={() => onUnitChange('Km')}
        >
          Km
        </UnitButton>
        <UnitButton
          selected={selectedUnit === 'Miles'}
          onClick={() => onUnitChange('Miles')}
        >
          Miles
        </UnitButton>
      </UnitSelector>
    </SelectorContainer>
  );
};

export default RadiusSelector;
